import { DecimalPipe } from '@angular/common';
import type { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, type OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from '@fino-ui/button';
import { FinoDatepickerComponent } from '@fino-ui/datepicker';
import { SubmittableForm } from '@fino-ui/forms';
import { TranslationModule } from '@fino-ui/translation';
import { SvgIconComponent } from 'angular-svg-icon';
import { Subscription } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { HeaderComponent } from 'src/app/components/header/header.component';
import { downloadCSVFromRequest } from 'src/app/core/common/csv.download';
import type { SustainabilityReport } from './sustainability.model';
import { SustainabilityService } from './sustainability.service';

@Component({
  selector: 'app-sustainability',
  templateUrl: './sustainability.component.html',
  styleUrl: './sustainability.component.sass',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    DecimalPipe,
    ButtonModule,
    TranslationModule,
    HeaderComponent,
    SvgIconComponent,
    ReactiveFormsModule,
    FinoDatepickerComponent,
  ],
})
export class SustainabilityComponent implements OnInit {
  constructor(
    private sustainabilityService: SustainabilityService,
    private appService: AppService,
  ) {}

  sustainabilityReport: SustainabilityReport;
  cancelDatePickerConfig = {};
  csvSubscription: Subscription = Subscription.EMPTY;

  get csvDownloading() {
    return !this.csvSubscription.closed;
  }

  items = [
    {
      name: 'wood',
      logo: '/assets/icons/icon_tree.svg',
      unit: 'kg',
      reference: 1 / 28, // gewicht eines billy regals
    },
    {
      name: 'paper',
      logo: '/assets/icons/icon_paper.svg',
      unit: 'Seiten',
      reference: 1 / 335, // seiten hat "Harry Potter und der Stein der Weisen"
    },
    {
      name: 'water',
      logo: '/assets/icons/icon_water.svg',
      unit: 'l',
      reference: 6.0 / 60, // 6 liter wasser beim duschen pro minute
    },
    {
      name: 'CO2',
      logo: '/assets/icons/icon_co2.svg',
      unit: 'kg',
      reference: 0.25, // 0.25kg CO2 pro flugmeile
    },
  ];

  dateForm = new SubmittableForm({
    from: new FormControl(''),
    to: new FormControl(''),
  });

  ngOnInit(): void {
    this.dateForm.valueChanges.subscribe(() => this.renderChart());

    const todayMinusYear = new Date();
    todayMinusYear.setFullYear(todayMinusYear.getFullYear() - 1);
    this.dateForm.patchValue({
      from: todayMinusYear,
      to: new Date(),
    });
  }

  downloadAsCSV() {
    const { to, from } = this.dateForm.value;
    this.csvSubscription = this.sustainabilityService
      .fetchSustainabilityStatisticsAsCSV(from, to)
      .subscribe(downloadCSVFromRequest);
  }

  renderChart() {
    const { to, from } = this.dateForm.value;

    this.sustainabilityService
      .fetchSustainabilityStatistics(from, to)
      .subscribe(
        (resp: SustainabilityReport) => {
          this.sustainabilityReport = resp;
        },
        (err: HttpErrorResponse) => {
          this.appService.error(err.error.message);
        },
      );
  }
}
